// Custom i18n hooks
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { i18n, SUPPORTED_LANGUAGES, SupportedLanguage, isRTLLanguage, getLanguageDirection } from './index';

/**
 * Hook for managing language state and switching
 */
export function useLanguage() {
  const { i18n: i18nInstance } = useTranslation();

  const currentLanguage = i18nInstance.language as SupportedLanguage;
  const languageInfo = SUPPORTED_LANGUAGES[currentLanguage] || SUPPORTED_LANGUAGES.he;

  const changeLanguage = useCallback(async (lang: SupportedLanguage) => {
    await i18nInstance.changeLanguage(lang);
  }, [i18nInstance]);

  const languages = useMemo(() => {
    return Object.entries(SUPPORTED_LANGUAGES).map(([code, info]) => ({
      code: code as SupportedLanguage,
      ...info,
      isActive: code === currentLanguage,
    }));
  }, [currentLanguage]);

  return {
    currentLanguage,
    languageInfo,
    changeLanguage,
    languages,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };
}

/**
 * Hook for RTL/LTR direction handling
 */
export function useDirection() {
  const { i18n: i18nInstance } = useTranslation();
  const currentLanguage = i18nInstance.language;

  const isRTL = useMemo(() => isRTLLanguage(currentLanguage), [currentLanguage]);
  const direction = useMemo(() => getLanguageDirection(currentLanguage), [currentLanguage]);

  // CSS logical property helpers
  const start = isRTL ? 'right' : 'left';
  const end = isRTL ? 'left' : 'right';

  // Tailwind class helpers
  const marginStart = isRTL ? 'mr' : 'ml';
  const marginEnd = isRTL ? 'ml' : 'mr';
  const paddingStart = isRTL ? 'pr' : 'pl';
  const paddingEnd = isRTL ? 'pl' : 'pr';
  const borderStart = isRTL ? 'border-r' : 'border-l';
  const borderEnd = isRTL ? 'border-l' : 'border-r';
  const textAlign = isRTL ? 'text-right' : 'text-left';
  const flexDirection = isRTL ? 'flex-row-reverse' : 'flex-row';

  return {
    isRTL,
    direction,
    start,
    end,
    marginStart,
    marginEnd,
    paddingStart,
    paddingEnd,
    borderStart,
    borderEnd,
    textAlign,
    flexDirection,
  };
}

/**
 * Hook for translation with namespace support
 */
export function useT(namespace?: string) {
  const { t, i18n: i18nInstance, ready } = useTranslation(namespace);

  return {
    t,
    i18n: i18nInstance,
    ready,
    language: i18nInstance.language as SupportedLanguage,
  };
}

/**
 * Hook for formatting numbers based on locale
 */
export function useNumberFormat() {
  const { i18n: i18nInstance } = useTranslation();
  const locale = i18nInstance.language;

  const formatNumber = useCallback((value: number, options?: Intl.NumberFormatOptions) => {
    return new Intl.NumberFormat(locale, options).format(value);
  }, [locale]);

  const formatCurrency = useCallback((value: number, currency = 'ILS') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(value);
  }, [locale]);

  const formatPercent = useCallback((value: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value);
  }, [locale]);

  return {
    formatNumber,
    formatCurrency,
    formatPercent,
  };
}

/**
 * Hook for formatting dates based on locale
 */
export function useDateFormat() {
  const { i18n: i18nInstance } = useTranslation();
  const locale = i18nInstance.language;

  const formatDate = useCallback((date: Date | string, options?: Intl.DateTimeFormatOptions) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, options).format(d);
  }, [locale]);

  const formatTime = useCallback((date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  }, [locale]);

  const formatDateTime = useCallback((date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  }, [locale]);

  const formatRelativeTime = useCallback((date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

    if (diffSec < 60) return rtf.format(-diffSec, 'second');
    if (diffMin < 60) return rtf.format(-diffMin, 'minute');
    if (diffHour < 24) return rtf.format(-diffHour, 'hour');
    if (diffDay < 7) return rtf.format(-diffDay, 'day');
    if (diffDay < 30) return rtf.format(-Math.floor(diffDay / 7), 'week');
    if (diffDay < 365) return rtf.format(-Math.floor(diffDay / 30), 'month');
    return rtf.format(-Math.floor(diffDay / 365), 'year');
  }, [locale]);

  return {
    formatDate,
    formatTime,
    formatDateTime,
    formatRelativeTime,
  };
}

// Re-export everything from index
export * from './index';
